import { FormErrors, useForm, UseFormReturnType } from '@mantine/form';
import { z } from 'zod';
import { getValidateErrors } from '@/lib/utils/validation';
import { useEffect, useMemo, useState } from 'react';
import { CustomerSettingType } from '@/services/enums';
import { customerSettingsApiArea } from '@/services/api/customerSettingsApiArea';
import { GetCustomerParams } from '@/services/api/customerSettingsApiArea/types';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

const schema = z.object({
  Host: z.string().nullable(),
  IsEnabled: z.boolean(),
  Port: z.number().optional().default(0),
  Format: z.number().default(0)
});

export type TFormValuesShareLinks = z.infer<typeof schema>;
type Return = [UseFormReturnType<TFormValuesShareLinks>, () => void, boolean, boolean, boolean];

export const useSettingSIEMSystem = (CustomerId?: number): Return => {
  const [isLoading, setLoading] = useState(true);
  const [isPartLoading, setIsPartLoading] = useState(false);
  const [initialValuesFromServer, setInitialValuesFromServer] =
    useState<TFormValuesShareLinks | null>(null);
  const { t } = useTranslation();

  const form = useForm<TFormValuesShareLinks>({
    validateInputOnChange: true,
    initialValues: {
      Host: '',
      Port: 0,
      Format: CustomerSettingType.None,
      IsEnabled: false
    },
    validate: (values): FormErrors => getValidateErrors(schema, values)
  });

  const loadSettings = async () => {
    const params: GetCustomerParams = {
      id: CustomerId,
      extended: true
    };

    const { Response } = await customerSettingsApiArea().GetCustomer(params);
    const settings = Response.Data.Settings;
    setLoading(false);

    if (!settings || !settings.ExportUserActionsSettings) return;

    const values: TFormValuesShareLinks = {
      Host: settings.ExportUserActionsSettings?.Host ?? '',
      Port: Number(settings.ExportUserActionsSettings?.Port ?? 0),
      Format: Number(settings.ExportUserActionsSettings?.Format ?? 0),
      IsEnabled: settings.ExportUserActionsSettings?.IsEnabled ?? false
    };

    form.setValues(values);
    setInitialValuesFromServer(values);
  };

  useEffect(() => {
    if (!CustomerId) return;
    setLoading(true);
    loadSettings().finally(() => setLoading(false));
  }, [CustomerId]);

  const update = () => loadSettings().finally(() => setIsPartLoading(false));

  const handleSubmit = async () => {
    if (!CustomerId) return;
    setIsPartLoading(true);
    const params = {
      CustomerId,
      Type: CustomerSettingType.ExportUserActionsSettings,
      Settings: {
        ExportUserActionsSettings: {
          Host: form.values.Host,
          Port: form.values.Port || 0,
          Format: form.values.Format,
          IsEnabled: form.values.IsEnabled
        }
      }
    };

    try {
      await customerSettingsApiArea().SaveCustomerSettings(params);
      await update();
      showNotification({
        title: t('SettingSIEMSystem.notificationTitle'),
        message: t('SettingSIEMSystem.success'),
        autoClose: true,
        color: 'green'
      });
    } catch (e) {
      showNotification({
        title: t('SettingSIEMSystem.notificationTitle'),
        message: t('SettingSIEMSystem.error'),
        autoClose: true,
        color: 'red'
      });
    }
  };

  const isFormChanged = useMemo(() => {
    if (!initialValuesFromServer) return false;

    return JSON.stringify(initialValuesFromServer) !== JSON.stringify(form.values);
  }, [form.values, initialValuesFromServer]);

  return [form, handleSubmit, isLoading, isPartLoading, isFormChanged];
};
